import type { PropsWithChildren } from 'react';
import { Children, cloneElement, useState } from 'react';

import CopyrightFooter from 'src/app/components/Elements/CopyrightFooter';
import { VerticalProgressBar } from 'src/app/components/Elements/ProgressBars/VerticalProgressBar/index';
import type { ThemeSpaceType } from 'src/styles/theme';

import { HorizontalProgressBar } from '../../Elements/ProgressBars/HorizontalProgressBar';

import { StepCount } from './StepCount';
import { EmptyCol, FooterRow, MainContainer, MainRow, MainSection, Sidebar } from './styles';

interface ProgressFormProps
  extends PropsWithChildren<{
    steps: (string | null)[];
    currentStepIndex: number;
    onPrev?: () => void;
    isHorizontalProgressBarVisible?: boolean;
    isHorizontalProgressBarHiddenOnDesktop?: boolean;
    isVerticalProgressBarVisible?: boolean;
    showPreviousButton?: boolean;
    hasStepHeader?: boolean;
    isStepCountVisible?: boolean;
    showCopyrightFooter?: boolean;
    mainRowPaddingBottom?: ThemeSpaceType;
    customHandlePrevText?: string;
  }> {}

const ProgressForm = ({
  steps,
  currentStepIndex,
  onPrev,
  isHorizontalProgressBarVisible = true,
  isVerticalProgressBarVisible = true,
  showPreviousButton = true,
  hasStepHeader = true,
  isStepCountVisible = true,
  showCopyrightFooter = true,
  mainRowPaddingBottom,
  children,
  customHandlePrevText,
}: ProgressFormProps) => {
  const [showHorizontalProgressBar, setShowHorizontalProgressBar] = useState(isHorizontalProgressBarVisible);
  const [showVerticalProgressBar, setShowVerticalProgressBar] = useState(isVerticalProgressBarVisible);

  const currentProgressBarIndex = currentStepIndex + 1;
  const currentProgressBarLength = steps?.length;
  const percentageFillForProgressBar = ((currentProgressBarIndex / currentProgressBarLength) * 100).toFixed(2);

  return (
    <>
      <HorizontalProgressBar
        currentStep={currentProgressBarIndex}
        totalSteps={currentProgressBarLength}
        fillPercent={percentageFillForProgressBar}
        size={'large'}
        hasLargeBorderRadius
        isHiddenOnDesktop={false}
      />
      <MainContainer>
        <MainRow mainRowPaddingBottom={mainRowPaddingBottom}>
          <Sidebar
            hasVerticalProgressBar={showVerticalProgressBar}
            hasHorizontalProgressBar={showHorizontalProgressBar}
          >
            <>
              {showVerticalProgressBar && (
                <VerticalProgressBar
                  currentStep={currentStepIndex + 1}
                  totalSteps={steps.length}
                  labels={steps}
                  height={steps.length * 5}
                />
              )}
            </>
          </Sidebar>

          <MainSection>
            {hasStepHeader && (
              <StepCount
                handlePrev={onPrev}
                currentStepIndex={currentStepIndex}
                totalSteps={steps.length}
                hideCount={!isStepCountVisible}
                showPreviousButton={showPreviousButton}
                customHandlePrevText={customHandlePrevText}
              />
            )}
            {Children.map(children, child =>
              cloneElement(child, { setShowHorizontalProgressBar, setShowVerticalProgressBar })
            )}
          </MainSection>
          <EmptyCol />
        </MainRow>

        {showCopyrightFooter && (
          <FooterRow>
            <CopyrightFooter isAlignedRight isUppercase marginRight={'8rem'} textColor={'gray'} />
          </FooterRow>
        )}
      </MainContainer>
    </>
  );
};

export default ProgressForm;
